/**
 * Create a tracking URL.
 *
 * A screen rather than a sheet, because it is the one thing in this module
 * people come to do on purpose — §61 describes somebody opening the workspace
 * specifically to make a link and paste it into WhatsApp — and because the UTM
 * builder wants room to show what it is building as it is built.
 *
 * The destination preview updates as you type, assembled exactly the way the
 * server assembles it, so what is previewed is what gets stored. The short URL
 * cannot be previewed: the code is minted by the server, from a CSPRNG, on
 * save. Showing a fake one would be the only dishonest pixel on the page.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { useTracking, changed, previewUrl, validDestination, slug } from '../../lib/urltracking.js';
import Icon from '../../lib/icons.jsx';
import { Card, CardHead, CardBody, EmptyState, Spinner } from '../../components/Primitives.jsx';
import { SOURCES, MEDIUMS } from '../../constants/urltracking.js';
import { CopyUrl, PresetInput, SourcePill, num } from './TrackBits.jsx';
import QrCode from './QrCode.jsx';

const BLANK = {
  campaign_id: '',
  name: '',
  destination_url: '',
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_term: '',
  utm_content: '',
  client_id: ''
};

export default function CreateLink() {
  const { setSheet, can, toast, rows } = useApp();
  const navigate = useNavigate();

  const campaigns = useTracking(() => api.urlTracking.campaigns.list({ limit: 500, status: '' }));
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [made, setMade] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const open = (campaigns.data?.data || []).filter(c => c.status !== 'Archived');
  /* The client book the app has already loaded — no second fetch, and no
     second idea of who the clients are. */
  const clients = (rows || []).filter(c => c && c.id && !c.archived).slice(0, 500);
  const chosen = open.find(c => c.id === form.campaign_id) || null;
  const preview = previewUrl(form.destination_url, form);
  const destinationOk = validDestination(form.destination_url);

  /* Named as the campaign is, unless somebody has typed their own. Saves the
     commonest keystroke without ever overwriting a deliberate value. */
  const pickCampaign = id => {
    const c = open.find(x => x.id === id);
    setForm(f => ({
      ...f,
      campaign_id: id,
      utm_campaign: f.utm_campaign || (c ? slug(c.name) : '')
    }));
  };

  const save = async () => {
    if (!form.campaign_id) { toast('Choose a campaign first'); return; }
    if (!destinationOk) { toast('The destination must be a full http:// or https:// address'); return; }
    for (const [key, label] of [['utm_source', 'source'], ['utm_medium', 'medium'], ['utm_campaign', 'campaign']]) {
      if (!String(form[key]).trim()) { toast(`Fill in the UTM ${label}`); return; }
    }

    setSaving(true);
    try {
      const row = await api.urlTracking.links.create(form);
      setMade(row);
      setForm({ ...BLANK, campaign_id: form.campaign_id });
      changed();
      toast('Tracking URL created');
    } catch (e) {
      toast(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!can('utm-links', 'create')) {
    return (
      <Card>
        <CardBody>
          <EmptyState icon="lock" tone="g-amber" title="Not your call">
            Your role can read tracking URLs but not create them. An admin can change that in
            Settings → Team &amp; roles.
          </EmptyState>
        </CardBody>
      </Card>
    );
  }

  if (campaigns.loading && !campaigns.data) return <p className="muted utload">Loading…</p>;

  if (!open.length) {
    return (
      <Card>
        <CardBody>
          <EmptyState icon="mega" tone="g-pink" title="No campaigns yet">
            <p>A tracking URL belongs to a campaign, so there has to be one first.</p>
            {can('utm-campaigns', 'create') ? (
              <button type="button" className="btn primary" onClick={() => setSheet({ kind: 'trackCampaign' })}>
                <Icon name="plus" />Create campaign
              </button>
            ) : null}
          </EmptyState>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      {made ? <MadeLink link={made} onDismiss={() => setMade(null)} /> : null}

      <div className="two utcreate">
        <div>
          <Card>
            <CardHead icon="link" tone="g-green" title="Basic information" />
            <CardBody>
              <div className="form">
                <div className="f wide">
                  <label htmlFor="ut-campaign">Campaign *</label>
                  <select id="ut-campaign" value={form.campaign_id} onChange={e => pickCampaign(e.target.value)}>
                    <option value="">Choose a campaign…</option>
                    {open.map(c => (
                      <option key={c.id} value={c.id}>{c.name}{c.status === 'Active' ? '' : ` (${c.status})`}</option>
                    ))}
                  </select>
                </div>

                <div className="f wide">
                  <label htmlFor="ut-dest">Destination URL *</label>
                  <input
                    id="ut-dest"
                    value={form.destination_url}
                    placeholder="https://example.com/offer"
                    onChange={e => set('destination_url', e.target.value)}
                  />
                  {form.destination_url && !destinationOk ? (
                    <small className="utbad">That is not a full http:// or https:// address.</small>
                  ) : null}
                </div>

                <div className="f wide">
                  <label htmlFor="ut-name">URL name</label>
                  <input
                    id="ut-name"
                    value={form.name}
                    placeholder="WhatsApp Message 1"
                    onChange={e => set('name', e.target.value)}
                  />
                  <small className="dim">Only you see this. It is how the link is listed and reported.</small>
                </div>

                {/*
                  * Optional, and last, because it should be. A campaign link
                  * belongs to nobody — §25 is right that tying every link to a
                  * client would be the wrong default. This is for the case
                  * where one link really is for one person; for a whole list,
                  * generate the link first and use "Per recipient" on it,
                  * which makes one code each in a single step.
                  */}
                <div className="f wide">
                  <label htmlFor="ut-client">Recipient <span className="dim">— optional</span></label>
                  <select id="ut-client" value={form.client_id} onChange={e => set('client_id', e.target.value)}>
                    <option value="">Nobody in particular — a public campaign link</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <small className="dim">
                    Ties this one link to one client, so its clicks are known to be theirs. Their name
                    never appears in the URL.
                  </small>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHead icon="target" tone="g-blue" title="UTM builder" />
            <CardBody>
              <p className="utnote">
                These tags travel with the visitor to the destination, so whatever analytics runs
                there can attribute the visit too. Keep the spelling consistent — utm_source=WhatsApp
                and utm_source=whatsapp are counted as two different sources.
              </p>

              <div className="form">
                <div className="f">
                  <label htmlFor="ut-source">UTM source *</label>
                  <PresetInput
                    id="ut-source"
                    value={form.utm_source}
                    onChange={v => set('utm_source', v)}
                    options={SOURCES}
                    placeholder="whatsapp"
                  />
                </div>
                <div className="f">
                  <label htmlFor="ut-medium">UTM medium *</label>
                  <PresetInput
                    id="ut-medium"
                    value={form.utm_medium}
                    onChange={v => set('utm_medium', v)}
                    options={MEDIUMS}
                    placeholder="marketing"
                  />
                </div>
                <div className="f">
                  <label htmlFor="ut-utmcamp">UTM campaign *</label>
                  <input
                    id="ut-utmcamp"
                    value={form.utm_campaign}
                    placeholder="diwali_2026"
                    onChange={e => set('utm_campaign', e.target.value)}
                  />
                </div>
                <div className="f">
                  <label htmlFor="ut-term">UTM term</label>
                  <input
                    id="ut-term"
                    value={form.utm_term}
                    placeholder="optional — the paid keyword"
                    onChange={e => set('utm_term', e.target.value)}
                  />
                </div>
                <div className="f wide">
                  <label htmlFor="ut-content">UTM content</label>
                  <input
                    id="ut-content"
                    value={form.utm_content}
                    placeholder="message_01"
                    onChange={e => set('utm_content', e.target.value)}
                  />
                  <small className="dim">
                    What tells two versions of the same push apart — message_01 against message_02.
                    This is the field that answers which wording worked.
                  </small>
                </div>
              </div>

              <div className="utchips">
                <span className="dim">Common sources:</span>
                {SOURCES.slice(0, 8).map(s => (
                  <button
                    key={s}
                    type="button"
                    className={`utchip${form.utm_source === s ? ' on' : ''}`}
                    onClick={() => set('utm_source', s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>

        <div>
          <Card>
            <CardHead icon="eye" tone="g-purple" title="Destination preview" />
            <CardBody>
              {preview ? (
                <>
                  <code className="utpreview">{preview}</code>
                  <p className="utnote">
                    Where the visitor lands, with the tags applied. Values are encoded for you —
                    type spaces and ampersands normally.
                  </p>
                </>
              ) : (
                <p className="muted">Enter a destination URL to see it assembled here.</p>
              )}

              <div className="utsummary">
                <div><span>Campaign</span><b>{chosen ? chosen.name : '—'}</b></div>
                <div>
                  <span>Source</span>
                  <b>{form.utm_source ? <SourcePill value={form.utm_source} /> : '—'}</b>
                </div>
                <div><span>Medium</span><b>{form.utm_medium || '—'}</b></div>
                <div><span>Content</span><b>{form.utm_content || '—'}</b></div>
              </div>

              <button type="button" className="btn primary wide" onClick={save} disabled={saving}>
                {saving ? <><Spinner />Generating…</> : <><Icon name="zap" />Generate tracking URL</>}
              </button>

              <p className="utnote">
                The short code is generated by the server when you save, so there is nothing to
                preview here yet.
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHead icon="alert" tone="g-amber" title="What this measures" />
            <CardBody>
              <p className="utnote">
                Link clicks, and only link clicks. Whether a WhatsApp message was delivered or read,
                or an email opened, is something only the messaging provider can tell you — those
                numbers live on the Marketing screens and are deliberately kept apart from these.
              </p>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}

/** The §51 confirmation: what was made, and the one thing to copy. */
function MadeLink({ link, onDismiss }) {
  const navigate = useNavigate();
  return (
    <Card className="utmade">
      <CardBody>
        <div className="utmadehead">
          <div className="ic g-green"><Icon name="check" solid /></div>
          <div>
            <h3>Tracking URL created</h3>
            <p className="dim">{link.name || 'Untitled'} · {link.campaign_name || ''}</p>
          </div>
          <button type="button" className="iconbtn" aria-label="Dismiss" onClick={onDismiss}>
            <Icon name="x" />
          </button>
        </div>

        <div className="utmadeurl">
          <div>
            <span className="dim">Short URL — this is the one to share</span>
            <code>{link.short_url}</code>
          </div>
          <CopyUrl url={link.short_url} />
        </div>

        <div className="utmadebody">
          <div>
            <div className="utsummary">
              <div><span>Destination</span><b>{link.destination_url}</b></div>
              <div><span>Source</span><b>{link.utm_source}</b></div>
              <div><span>Medium</span><b>{link.utm_medium}</b></div>
              <div><span>Clicks</span><b>{num(0)}</b></div>
            </div>

            <div className="utmadeacts">
              <button type="button" className="btn" onClick={() => navigate('/url-tracking/links')}>
                <Icon name="list" />See all tracking URLs
              </button>
              <span className="dim">Paste the short URL into WhatsApp, an email, an ad or a poster.</span>
            </div>
          </div>

          {/* The QR is offered here rather than behind a button, because a
              printed campaign is the one case where the short URL on its own
              is no use — nobody types a link off a poster. */}
          <QrCode url={link.short_url} name={link.name || link.short_code} size={150} />
        </div>
      </CardBody>
    </Card>
  );
}
